"use client";

import type { FC, KeyboardEvent } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { PenLinear } from "solar-icon-set";
import { cn } from "../../lib/utils";

const sizeClasses = {
  sm: {
    text: "text-base sm:text-sm px-2 py-1",
    input: "text-base sm:text-sm px-2 py-1",
    icon: 14,
  },
  md: {
    text: "text-base px-2 py-1.5",
    input: "text-base px-2 py-1.5",
    icon: 16,
  },
  lg: {
    text: "text-2xl font-semibold px-2 py-2",
    input: "text-2xl font-semibold px-2 py-2",
    icon: 20,
  },
};

export interface InlineEditableInputProps {
  /** The current value */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Placeholder text when value is empty */
  placeholder?: string;
  /** Error message to display */
  errorMessage?: string;
  /** Whether the input is in an error state */
  isInvalid?: boolean;
  /** Whether the input is disabled */
  isDisabled?: boolean;
  /** Additional class name for the container */
  className?: string;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Aria label for accessibility */
  "aria-label"?: string;
  /** Callback when input loses focus */
  onBlur?: () => void;
  /** Callback when Enter key is pressed */
  onSubmit?: () => void;
  /** When true, text wraps and input grows vertically. Uses textarea instead of input. */
  multiline?: boolean;
}

export const InlineEditableInput: FC<InlineEditableInputProps> = ({
  value,
  onChange,
  placeholder = "Click to edit",
  errorMessage,
  isInvalid = false,
  isDisabled = false,
  className,
  size = "md",
  "aria-label": ariaLabel,
  onBlur,
  onSubmit,
  multiline = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [internalValue, setInternalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const editableRef = useRef<HTMLDivElement>(null);
  const errorId = `${useId()}-error`;

  // Sync internal value with external value
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Focus input/contenteditable when entering edit mode
  useEffect(() => {
    if (isEditing) {
      if (multiline && editableRef.current) {
        // Set content before focusing (avoid React re-render issues)
        editableRef.current.textContent = value;
        editableRef.current.focus();
        // Place cursor at end of content
        const range = document.createRange();
        range.selectNodeContents(editableRef.current);
        range.collapse(false); // false = collapse to end
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      } else if (inputRef.current) {
        inputRef.current.focus();
        // Place cursor at end
        const len = inputRef.current.value.length;
        inputRef.current.setSelectionRange(len, len);
      }
    }
  }, [isEditing, multiline, value]);

  const handleClick = useCallback(() => {
    if (!isDisabled) {
      setIsEditing(true);
    }
  }, [isDisabled]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    if (internalValue !== value) {
      onChange(internalValue);
    }
    onBlur?.();
  }, [internalValue, value, onChange, onBlur]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        setIsEditing(false);
        if (internalValue !== value) {
          onChange(internalValue);
        }
        onSubmit?.();
      } else if (e.key === "Escape") {
        e.preventDefault();
        setInternalValue(value);
        setIsEditing(false);
      }
    },
    [internalValue, value, onChange, onSubmit],
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
  }, []);

  const handleEditableInput = useCallback(() => {
    if (editableRef.current) {
      setInternalValue(editableRef.current.textContent || "");
    }
  }, []);

  const handleEditableBlur = useCallback(() => {
    const newValue = editableRef.current?.textContent || "";
    setIsEditing(false);
    if (newValue !== value) {
      onChange(newValue);
    }
    onBlur?.();
  }, [value, onChange, onBlur]);

  const handleEditableKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (editableRef.current) {
          editableRef.current.textContent = value;
        }
        setInternalValue(value);
        setIsEditing(false);
      }
    },
    [value],
  );

  const sizeConfig = sizeClasses[size];
  const hasError = isInvalid || !!errorMessage;
  const displayValue = value || placeholder;
  const isEmpty = !value;

  // For multiline mode, use overlay approach to prevent layout shift
  if (multiline) {
    // When editing, show internalValue in button so it grows with typing
    const buttonText = isEditing ? internalValue || placeholder : displayValue;

    return (
      <div className={cn("flex flex-col", className)}>
        <div className="relative">
          {/* Button always rendered to maintain consistent size - shows current editing value */}
          <button
            type="button"
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            disabled={isDisabled || isEditing}
            aria-label={ariaLabel || `Edit ${displayValue}`}
            aria-hidden={isEditing}
            tabIndex={isEditing ? -1 : 0}
            className={cn(
              "group flex w-full cursor-text items-start gap-2 rounded-medium border-1 border-transparent text-left transition-colors",
              sizeConfig.text,
              isEditing && "invisible",
              hasError && !isEditing && "border-danger",
              !hasError && !isDisabled && !isEditing && "hover:bg-default-100",
              isDisabled && "cursor-not-allowed opacity-50",
              isEmpty && !isEditing && "text-default-400",
            )}
          >
            <span className="wrap-anywhere flex-1 whitespace-pre-wrap">{buttonText}</span>
            {!isDisabled && (
              <span
                className={cn("shrink-0 text-default-500 transition-opacity", isHovered ? "opacity-100" : "opacity-0")}
              >
                <PenLinear size={sizeConfig.icon} />
              </span>
            )}
          </button>

          {/* ContentEditable overlays button when editing */}
          {isEditing && (
            // biome-ignore lint/a11y/useSemanticElements: contentEditable requires div for natural sizing
            <div
              ref={editableRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleEditableInput}
              onBlur={handleEditableBlur}
              onKeyDown={handleEditableKeyDown}
              role="textbox"
              aria-label={ariaLabel}
              aria-multiline="true"
              aria-invalid={hasError}
              aria-describedby={errorMessage ? errorId : undefined}
              tabIndex={0}
              className={cn(
                "wrap-anywhere absolute inset-0 overflow-hidden rounded-medium border-1 bg-background outline-none transition-colors",
                sizeConfig.input,
                hasError ? "border-danger focus:border-danger" : "border-default-300 focus:border-primary",
                "empty:before:text-default-400 empty:before:content-[attr(data-placeholder)]",
              )}
              data-placeholder={placeholder}
            />
          )}
        </div>
        {errorMessage && (
          <span id={errorId} className="mt-1 text-danger text-xs">
            {errorMessage}
          </span>
        )}
      </div>
    );
  }

  // Default single-line mode with overlay approach
  return (
    <div className={cn("flex flex-col", className)}>
      <div className="relative">
        <button
          type="button"
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={isDisabled || isEditing}
          aria-label={ariaLabel || `Edit ${displayValue}`}
          aria-hidden={isEditing}
          tabIndex={isEditing ? -1 : 0}
          className={cn(
            "group flex w-full cursor-text items-start gap-2 rounded-medium border-1 border-transparent text-left transition-colors",
            sizeConfig.text,
            isEditing && "invisible",
            hasError && !isEditing && "border-danger",
            !hasError && !isDisabled && !isEditing && "hover:bg-default-100",
            isDisabled && "cursor-not-allowed opacity-50",
            isEmpty && "text-default-400",
          )}
        >
          <span className="flex-1 truncate">{displayValue}</span>
          {!isDisabled && (
            <span
              className={cn("shrink-0 text-default-500 transition-opacity", isHovered ? "opacity-100" : "opacity-0")}
            >
              <PenLinear size={sizeConfig.icon} />
            </span>
          )}
        </button>

        {isEditing && (
          <input
            ref={inputRef}
            type="text"
            value={internalValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isDisabled}
            aria-label={ariaLabel}
            aria-invalid={hasError}
            aria-describedby={errorMessage ? errorId : undefined}
            className={cn(
              "absolute inset-0 w-full rounded-medium border-1 bg-background outline-none transition-colors",
              sizeConfig.input,
              hasError ? "border-danger focus:border-danger" : "border-default-300 focus:border-primary",
            )}
          />
        )}
      </div>
      {errorMessage && (
        <span id={errorId} className="mt-1 text-danger text-xs">
          {errorMessage}
        </span>
      )}
    </div>
  );
};
